import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { legacyWorkScheduleColors, legacyWorkScheduleStatuses, legacyWorkScheduleTypes } from '../../mock/legacyWorkSchedule'
import {
  legacyWorkScheduleEditRecord,
  legacyWorkScheduleFormCustomers,
  legacyWorkScheduleFormGroups,
  legacyWorkScheduleFormOffice,
  legacyWorkScheduleFormStaff,
  type LegacyWorkScheduleChoice,
  type LegacyWorkScheduleCustomer,
  type LegacyWorkScheduleFormType,
} from '../../mock/legacyWorkScheduleForm'
import './LegacyWorkSchedule.css'

const GENERAL_TYPE: LegacyWorkScheduleFormType = 'l_scheduleTypeGeneral'
const WORK_TYPE: LegacyWorkScheduleFormType = 'l_scheduleTypeWorkDaily'
const WAIT_TYPE: LegacyWorkScheduleFormType = 'l_scheduleTypeShootingWait'
const SHOOT_TYPE: LegacyWorkScheduleFormType = 'l_scheduleTypeShoot'

const typeAliases: Record<string, LegacyWorkScheduleFormType> = {
  general: GENERAL_TYPE,
  normal: GENERAL_TYPE,
  '일반일정': GENERAL_TYPE,
  l_scheduleTypeGeneral: GENERAL_TYPE,
  work: WORK_TYPE,
  daily: WORK_TYPE,
  '업무일지': WORK_TYPE,
  l_scheduleTypeWorkDaily: WORK_TYPE,
  waiting: WAIT_TYPE,
  holiday: WAIT_TYPE,
  leave: WAIT_TYPE,
  '휴무': WAIT_TYPE,
  '촬영대기': WAIT_TYPE,
  l_scheduleTypeHoliday: WAIT_TYPE,
  l_scheduleTypeShootingWait: WAIT_TYPE,
  shoot: SHOOT_TYPE,
  photo: SHOOT_TYPE,
  '촬영일정': SHOOT_TYPE,
  '촬영스케줄': SHOOT_TYPE,
  l_scheduleTypeShoot: SHOOT_TYPE,
}

const typeQueryValues: Record<LegacyWorkScheduleFormType, string> = {
  [GENERAL_TYPE]: 'general',
  [WORK_TYPE]: 'work',
  [WAIT_TYPE]: 'holiday',
  [SHOOT_TYPE]: 'shoot',
}

function validDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return false
  const [, year, month, day] = match.map(Number)
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
}

function localToday() {
  const date = new Date()
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function queryTime(params: URLSearchParams) {
  const hour = params.get('hour')
  const minute = params.get('minute')
  const value = hour && minute ? `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}` : ''
  return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value) ? value : null
}

function plusHour(value: string) {
  const [hour, minute] = value.split(':').map(Number)
  return `${String((hour + 1) % 24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function initialForm() {
  const params = new URLSearchParams(window.location.search)
  const isEdit = Boolean(params.get('schedule_id')?.trim())
  const requestedType = params.get('schedule_type')?.trim() || params.get('shcedule_type')?.trim() || ''
  const type = typeAliases[requestedType] ?? (isEdit ? legacyWorkScheduleEditRecord.type : GENERAL_TYPE)
  const requestedDate = params.get('date')?.trim() || ''
  const date = validDate(requestedDate)
    ? requestedDate
    : isEdit && validDate(legacyWorkScheduleEditRecord.date) ? legacyWorkScheduleEditRecord.date : localToday()
  const requestedStart = queryTime(params)
  const startTime = requestedStart ?? (isEdit ? legacyWorkScheduleEditRecord.startTime : '09:00')
  const hasCustomerQuery = Boolean(params.get('customer_id')?.trim())
  const requestedStaff = params.get('staff_id') ?? ''
  const requestedWorkgroup = params.get('working_group_id') ?? ''
  return {
    isEdit,
    scheduleId: params.get('schedule_id')?.trim() ?? '',
    type,
    date,
    startTime,
    endTime: requestedStart ? plusHour(requestedStart) : isEdit ? legacyWorkScheduleEditRecord.endTime : '10:00',
    title: isEdit ? legacyWorkScheduleEditRecord.title : '',
    memo: isEdit ? legacyWorkScheduleEditRecord.memo : '',
    content: isEdit ? legacyWorkScheduleEditRecord.content : '',
    color: isEdit ? legacyWorkScheduleEditRecord.colorIndex : 0,
    status: isEdit ? legacyWorkScheduleEditRecord.statusIndex : 0,
    staffId: isEdit ? legacyWorkScheduleEditRecord.staffId : (legacyWorkScheduleFormStaff.some((item) => item.id === requestedStaff) ? requestedStaff : legacyWorkScheduleFormStaff[0]?.id ?? ''),
    workgroupId: isEdit ? legacyWorkScheduleEditRecord.workgroupId : (legacyWorkScheduleFormGroups.some((item) => item.id === requestedWorkgroup) ? requestedWorkgroup : ''),
    customer: hasCustomerQuery ? legacyWorkScheduleFormCustomers[0] : null,
  }
}

function openShootSchedule(date: string, startTime: string, staffId: string, workgroupId: string, customer: LegacyWorkScheduleCustomer | null) {
  const [hour, minute] = startTime.split(':')
  const params = new URLSearchParams({
    CRUD: '1',
    office_name: legacyWorkScheduleFormOffice.name,
    office_id: legacyWorkScheduleFormOffice.id,
    staff_id: staffId,
    hour,
    minute,
    date,
    working_group_id: workgroupId,
    caller: 'work_schedule',
  })
  if (customer) params.set('customer_id', customer.id)
  window.location.assign(`/v1/shoot_schedule?${params.toString()}`)
}

export function LegacyWorkSchedule() {
  const initial = useMemo(initialForm, [])
  const [type, setType] = useState<LegacyWorkScheduleFormType>(initial.type)
  const [status, setStatus] = useState(initial.status)
  const [date, setDate] = useState(initial.date)
  const [startTime, setStartTime] = useState(initial.startTime)
  const [endTime, setEndTime] = useState(initial.endTime)
  const [title, setTitle] = useState(initial.title)
  const [memo, setMemo] = useState(initial.memo)
  const [content, setContent] = useState(initial.content)
  const [color, setColor] = useState(initial.color)
  const [colorOpen, setColorOpen] = useState(false)
  const [staffId, setStaffId] = useState(initial.staffId)
  const [workgroupId, setWorkgroupId] = useState(initial.workgroupId)
  const [customer, setCustomer] = useState<LegacyWorkScheduleCustomer | null>(initial.customer)
  const [formError, setFormError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [deleted, setDeleted] = useState(false)

  const showCustomerSection = !(initial.isEdit && !initial.customer)
  const isWork = type === WORK_TYPE
  const isGeneral = type === GENERAL_TYPE

  useEffect(() => {
    if (initial.type === SHOOT_TYPE) openShootSchedule(initial.date, initial.startTime, initial.staffId, initial.workgroupId, initial.customer)
  }, [initial])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (!params.has('show_customer_info')) {
      params.set('show_customer_info', initial.customer ? '1' : '0')
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`)
    }
  }, [initial.customer])

  useEffect(() => {
    const receiveCustomer = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.data?.type !== 'legacy-work-schedule-customer-select') return
      setCustomer(event.data.customer as LegacyWorkScheduleCustomer)
    }
    window.addEventListener('message', receiveCustomer)
    return () => window.removeEventListener('message', receiveCustomer)
  }, [])

  const selectType = (id: string) => {
    const nextType = typeAliases[id] ?? GENERAL_TYPE
    const params = new URLSearchParams(window.location.search)
    params.set('schedule_type', typeQueryValues[nextType])
    params.delete('shcedule_type')
    params.set('date', date)
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`)
    setType(nextType)
    setFormError(null)
    if (nextType === SHOOT_TYPE) openShootSchedule(date, startTime, staffId, workgroupId, customer)
  }

  const validate = () => {
    if (isWork && !staffId) return '담당자를 선택해주세요.'
    if (!title.trim()) return '제목을 입력해주세요.'
    if (!validDate(date)) return '일정을 YYYY-MM-DD 형식의 올바른 날짜로 입력해주세요.'
    if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(startTime) || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(endTime)) return '시각을 HH:MM 형식으로 입력해주세요.'
    return null
  }

  const saveOrModify = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const error = validate()
    setFormError(error)
    if (error) return
    const action = initial.isEdit ? '수정' : '저장'
    if (!window.confirm(`${action}하시겠습니까?`)) return
    setToast(`Mock 업무스케줄 ${action}이 완료되었습니다.`)
  }

  const deleteSchedule = () => {
    if (!window.confirm('삭제하시겠습니까?')) return
    setDeleted(true)
    setToast('Mock 업무스케줄 삭제가 완료되었습니다.')
  }

  const openCustomer = () => {
    if (!customer) return
    window.open(`/v1/customer/?CRUD=2&id=${encodeURIComponent(customer.id)}&caller=work_schedule`, '_blank')
  }

  const openCustomerSearch = () => {
    const params = new URLSearchParams({
      office_id: legacyWorkScheduleFormOffice.id,
      page_no: '1', page_start: '1', page_count: '30', search_type: '1', caller: 'work_schedule',
    })
    window.open(`/v1/customer_search/?${params.toString()}`, '_blank', 'width=1200,height=850,scrollbars=yes')
  }

  const openHistory = () => {
    const params = new URLSearchParams({ CRUD: '1', schedule_id: initial.scheduleId || 'mock-schedule' })
    window.open(`/v1/work_schedule_history?${params.toString()}`, '_blank', 'width=600,height=250,location=no,status=no,scrollbars=yes')
  }

  return <main className="main_wrapper g_main_content l_main_work_schedule legacy-s03-main">
    <div className="g_title">업무스케줄</div>
    <section className={`g_card s03-card${initial.isEdit ? ' is-edit' : ''}${showCustomerSection ? '' : ' no-customer-section'}`}>
      {showCustomerSection && <div className={`l_wrap_customer_info s03-customer-info${customer ? '' : ' no_customer'}`}>
        <div className="g_row"><div className="g_input_wrap"><span className="g_label title center">고객</span>
          {customer
            ? <div id="l_customerNo" className="l_customer_row_container s03-selected-customer">
              <button className="l_customer_row g_item" type="button" onClick={openCustomer}>아기: {customer.babyName}</button>
              <button className="l_customer_row g_item" type="button" onClick={openCustomer}>보호자: {customer.guardianName}</button>
              <span>({customer.customerNo})</span>
            </div>
            : <div className="l_search_customer"><button id="l_cutomer_search" className="g_btn search size_2" type="button" onClick={openCustomerSearch}>고객검색</button><span className="g_text comment">* 고객을 검색하고 일정을 등록하세요.</span></div>}
        </div></div>
      </div>}
      <div className="l_wrap_work_schedule_info"><form id="l_workScheduleInfoForm" onSubmit={saveOrModify}>
        <S03Row label="스케줄종류"><div className="s03-radio-wrap" role="radiogroup" aria-label="스케줄종류">{legacyWorkScheduleTypes.map((item) => <div className="radio_itme_wrap" key={item.id}><label><input className="g_input radiobox" type="radio" name="schedule_type" id={item.id} checked={type === item.id} onChange={() => selectType(item.id)} /><span className="g_text">{item.label}</span></label></div>)}</div></S03Row>
        <S03Row label="지점" labelFor="l_officeId"><input id="l_officeId" className="g_input width_2x" type="text" value={legacyWorkScheduleFormOffice.name} disabled readOnly /></S03Row>
        {isWork
          ? <S03Row rowId="l_staffSelect" label="담당자" labelFor="l_staff_button" required><S03Chosen id="l_staff" value={staffId} options={legacyWorkScheduleFormStaff} placeholder="-" onChange={setStaffId} required /></S03Row>
          : <div id="l_staffSelect" className="g_row s03-empty-dynamic-row" aria-hidden="true" />}
        {isGeneral
          ? <S03Row rowId="l_workgroupSelect" label="작업담당" labelFor="l_working_group_button"><S03Chosen id="l_working_group" value={workgroupId} options={legacyWorkScheduleFormGroups} placeholder="-" onChange={setWorkgroupId} /></S03Row>
          : <div id="l_workgroupSelect" className="g_row s03-empty-dynamic-row" aria-hidden="true" />}
        <S03Row label="제목" labelFor="l_title" required><input id="l_title" className="g_input width_2x" type="text" value={title} required aria-invalid={Boolean(formError && !title.trim())} onBlur={() => { if (!title.trim()) setFormError('제목을 입력해주세요.') }} onChange={(event) => { setTitle(event.target.value); if (event.target.value.trim()) setFormError(null) }} /></S03Row>
        <S03Row label="일정" labelFor="l_scheduleDate"><input id="l_scheduleDate" className="g_input g_input_datepicker s03-date" type="text" title="YYYY-MM-DD" pattern="^\d{4}-\d{2}-\d{2}$" value={date} onChange={(event) => setDate(event.target.value)} /><input id="l_photoStart" className="g_input g_input_timepicker g_input_timepicker_start s03-time" aria-label="시작 시각" type="text" pattern="^(?:[01]\d|2[0-3]):[0-5]\d$" value={startTime} onChange={(event) => setStartTime(event.target.value)} /><span className="s03-tilde">~</span><input id="l_photoEnd" className="g_input g_input_timepicker g_input_timepicker_end s03-time" aria-label="종료 시각" type="text" pattern="^(?:[01]\d|2[0-3]):[0-5]\d$" value={endTime} onChange={(event) => setEndTime(event.target.value)} /></S03Row>
        <S03Row label="색상"><span id="color_selector" className="color_selector l_js_e_schedule_color_list"><button type="button" className="g_item selected_color_box" style={{ backgroundColor: color === 0 ? '#fff' : legacyWorkScheduleColors[color - 1] }} onClick={() => setColorOpen((value) => !value)} aria-expanded={colorOpen} aria-haspopup="listbox" aria-label="스케줄 색상 선택" />{colorOpen && <span className="color_box g_scroll_bar" role="listbox" aria-label="Mock 색상 목록"><table className="l_js_e_table"><tbody className="l_js_e_color_list"><tr className="l_remove_color text_color_select" role="option" aria-selected={color === 0} tabIndex={0} onKeyDown={(event) => activateRow(event, () => { setColor(0); setColorOpen(false) })} onClick={() => { setColor(0); setColorOpen(false) }}><td className="color_count">-</td><td className="color_status">미지정</td></tr>{legacyWorkScheduleColors.map((value, index) => <tr className="text_color_select" role="option" aria-selected={color === index + 1} tabIndex={0} style={{ backgroundColor: value }} key={`${value}-${index}`} onKeyDown={(event) => activateRow(event, () => { setColor(index + 1); setColorOpen(false) })} onClick={() => { setColor(index + 1); setColorOpen(false) }}><td className="color_count">{index + 1}</td><td className="color_status">Mock 색상</td></tr>)}</tbody></table></span>}</span></S03Row>
        <S03Row label="상태"><div id="l_schedule_status" className="s03-radio-wrap s03-status-wrap" role="radiogroup" aria-label="스케줄 상태">{legacyWorkScheduleStatuses.map((label, index) => <div className="radio_itme_wrap" key={label}><label><input className="g_input radiobox" type="radio" name="schedule_status" checked={status === index} onChange={() => setStatus(index)} /><span className="g_text">{label}</span></label></div>)}</div></S03Row>
        <div className="g_row w_full s03-memo-row"><div className="g_input_wrap"><label className="g_label title center" htmlFor="l_memo">메모</label><input id="l_memo" className="g_input" type="text" value={memo} onChange={(event) => setMemo(event.target.value)} /></div><p className="g_text comment fixed_width">* 스케줄에 표시됨</p></div>
        <S03Row label="내용" labelFor="l_scheduleContent" wide><textarea id="l_scheduleContent" className="g_input_textarea" value={content} onChange={(event) => setContent(event.target.value)} /></S03Row>
        {formError && <div className="s03-form-error" role="alert">{formError}</div>}
      </form></div>
      {initial.isEdit && <div className="l_wrap_work_schedule_history">
        <div className="g_row"><div className="g_input_wrap"><span className="g_label title center">등록일시</span></div><p id="l_insert_info">{legacyWorkScheduleEditRecord.insertedAt}</p></div>
        <div className="g_row"><div className="g_input_wrap"><span className="g_label title center">최근변경</span></div><p id="l_update_info">{legacyWorkScheduleEditRecord.updatedAt}</p><button className="g_btn size_2" id="l_workScheduleHistory" type="button" onClick={openHistory}>내역</button></div>
      </div>}
      <div className="l_wrap_work_schedule_bottom"><div className="g_btn_wrap">
        {initial.isEdit
          ? <><button className="g_btn modify" id="l_workScheduleModifyBtn" type="submit" form="l_workScheduleInfoForm" disabled={deleted}>수정</button><button className="g_btn delete" id="l_workScheduleDeleteBtn" type="button" disabled={deleted} onClick={deleteSchedule}>삭제</button></>
          : <button className="g_btn save" id="l_workScheduleSaveBtn" type="submit" form="l_workScheduleInfoForm">저장</button>}
        <button className="g_btn close" id="l_workScheduleCloseBtn" type="button" onClick={() => { setToast('Mock 일정 창을 닫습니다.'); window.close() }}>닫기</button>
      </div></div>
    </section>

    {toast && <div className="s03-toast" role="status">{toast}<button type="button" onClick={() => setToast(null)} aria-label="알림 닫기">×</button></div>}
  </main>
}

function S03Chosen({ id, value, options, placeholder, onChange, required = false }: {
  id: string
  value: string
  options: LegacyWorkScheduleChoice[]
  placeholder: string
  onChange: (value: string) => void
  required?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const wrapRef = useRef<HTMLDivElement>(null)
  const selected = options.find((item) => item.id === value)
  const filtered = options.filter((item) => item.label.toLowerCase().includes(search.trim().toLowerCase()))

  useEffect(() => {
    if (!open) return
    const closeOutside = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', closeOutside)
    return () => document.removeEventListener('mousedown', closeOutside)
  }, [open])

  return <><select id={id} className="s03-native-select-hidden" value={value} onChange={(event) => onChange(event.target.value)} tabIndex={-1} aria-hidden="true"><option value="">{placeholder}</option>{options.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select><div id={`${id}_chosen`} className={`chosen-container chosen-container-single s03-chosen${open ? ' chosen-with-drop chosen-container-active' : ''}`} ref={wrapRef}>
    <button id={`${id}_button`} className={`chosen-single ${selected ? '' : 'chosen-default'}`} type="button" aria-haspopup="listbox" aria-expanded={open} aria-controls={`${id}_results`} aria-required={required} onClick={() => setOpen((valueOpen) => !valueOpen)}><span>{selected?.label ?? placeholder}</span><div><b /></div></button>
    {open && <div className="chosen-drop"><div className="chosen-search"><input className="chosen-search-input" type="text" value={search} onChange={(event) => setSearch(event.target.value)} autoFocus aria-label={`${selected?.label ?? placeholder} 목록 검색`} /></div><ul id={`${id}_results`} className="chosen-results" role="listbox">{filtered.map((item, index) => <li role="presentation" key={item.id}><button className={index === 0 ? 'highlighted' : ''} type="button" role="option" aria-selected={item.id === value} onClick={() => { onChange(item.id); setOpen(false); setSearch('') }}>{item.label}</button></li>)}</ul></div>}
  </div></>
}

function activateRow(event: ReactKeyboardEvent<HTMLTableRowElement>, callback: () => void) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  callback()
}

function S03Row({ rowId, label, labelFor, children, required = false, wide = false }: { rowId?: string; label: string; labelFor?: string; children: ReactNode; required?: boolean; wide?: boolean }) {
  return <div id={rowId} className={`g_row ${wide ? 'w_full' : ''}`}><div className={`g_input_wrap ${required ? 'required' : ''}`}>{labelFor ? <label className="g_label title center" htmlFor={labelFor}>{label}</label> : <span className="g_label title center">{label}</span>}{children}</div></div>
}
